import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Lock, PlayCircle, PauseCircle, ImageIcon, LogOut, Home, BookOpen, Settings } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ornament } from "../components/Ornament";
import { storytellerDisplayName, storytellerPossessive } from "../lib/storyteller";
import { getStoryWeekDays, STORY_WEEK_CHAPTERS } from "../data/storyWeek";

/** MY LIVING BOOK — private home for a Story Week user.
 *  Shows all 7 days: answered (tick + preview + listen + edit), the current
 *  unanswered day (Ready now → Answer), or locked (with exact unlock time).
 *  Never exposes public URLs. Audio streams from the private authenticated
 *  /api/files endpoint (cookies sent via `withCredentials`). */
export default function MyLivingBook() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const DAYS = getStoryWeekDays();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=%2Fmy-living-book", { replace: true }); return; }
    (async () => {
      try {
        const res = await api.get("/story_week/mine");
        setSession(res.data);
      } catch (e) {
        setError("We couldn't load your Living Book. Please try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const doSignOut = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    navigate("/", { replace: true });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6" data-testid="mlb-loading">
        <p className="italic text-[#5C5548]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.25rem" }}>
          Opening your Living Book…
        </p>
      </div>
    );
  }

  const days = session?.days || {};
  const skipped = new Set(session?.skipped_days || []);
  const availableDay = session?.available_day || 1;
  const isVerified = !!user?.email_verified;
  const nextUnlockAt = session?.next_unlock_at;
  const dayUnlockAt = session?.day_unlock_at || {};
  const deliveryTime = session?.delivery_time || "08:00";
  const doneCount = Object.keys(days).length;

  // Format an HH:MM string as e.g. "8:00am".
  const formatTimeShort = (hhmm) => {
    if (!hhmm) return "8:00am";
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
    const period = h >= 12 ? "pm" : "am";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m.toString().padStart(2, "0")}${period}`;
  };

  // "Opens tomorrow at 8:00am" | "Opens on Friday 3 October at 8:00am".
  const formatUnlockShort = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const time = formatTimeShort(deliveryTime);
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const daysDiff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - startOfToday) / 86400000);
      if (daysDiff <= 1) return `Opens tomorrow at ${time}`;
      const wd = d.toLocaleDateString(undefined, { weekday: "long" });
      const dm = d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
      return `Opens on ${wd} ${dm} at ${time}`;
    } catch { return ""; }
  };

  // Legacy — kept for the "next locked day" summary panels that used the old prop.
  const formatUnlock = formatUnlockShort;

  return (
    <div className="min-h-screen paper" data-testid="my-living-book-page">
      {/* Signed-in nav */}
      <header className="border-b border-[#E8D5A3] bg-[#FAF7F2]" data-testid="mlb-nav">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-6 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="mlb-brand">
            <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
            <span className="text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", letterSpacing: "0.06em" }}>
              The Living Book
            </span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/" className="label-ui text-[12px] text-[#1B2A4A] hover:text-[#B8973A] transition-colors inline-flex items-center gap-2" style={{ minHeight: 44 }} data-testid="mlb-nav-home">
              <Home size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>Home</span>
            </Link>
            <Link to="/my-living-book" className="label-ui text-[12px] text-[#B8973A] inline-flex items-center gap-2" style={{ minHeight: 44 }} aria-current="page" data-testid="mlb-nav-book">
              <BookOpen size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>My Living Book</span>
            </Link>
            <Link to="/settings/delivery" className="label-ui text-[12px] text-[#1B2A4A] hover:text-[#B8973A] transition-colors inline-flex items-center gap-2" style={{ minHeight: 44 }} data-testid="mlb-nav-delivery">
              <Settings size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>Delivery settings</span>
            </Link>
            <button onClick={doSignOut} className="label-ui text-[12px] text-[#1B2A4A] hover:text-[#B8973A] transition-colors inline-flex items-center gap-2" style={{ minHeight: 44 }} data-testid="mlb-nav-signout">
              <LogOut size={16} strokeWidth={1.5} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <header className="text-center">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em]" data-testid="mlb-eyebrow">STORY WEEK</p>
          <h1 className="mt-6 text-4xl sm:text-6xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400, lineHeight: 1.08 }} data-testid="mlb-heading">
            MY LIVING BOOK
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.6 }} data-testid="mlb-subheading">
            {(() => {
              const n = storytellerDisplayName(user);
              const isSelf = !!user?.storyteller?.self_story;
              if (!n) return <>Your Story Week answers, recordings and memories&mdash;all in one place.</>;
              if (isSelf) return <>Your Story Week answers, recordings and memories&mdash;all in one place.</>;
              return <>{storytellerPossessive(user)} Story Week answers, recordings and memories&mdash;all in one place.</>;
            })()}
          </p>
          <Ornament className="my-10" />
        </header>

        {/* Verification notice — only when unverified AND already answered Day 1 */}
        {!isVerified && doneCount >= 1 && (
          <div className="mb-8 p-5 max-w-2xl mx-auto text-left" style={{ background: "#F7F0E0", borderLeft: "3px solid #B8973A", borderRadius: 2 }} data-testid="mlb-verify-notice">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.25em] mb-2">CONFIRM YOUR EMAIL</p>
            <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.6 }}>
              Please confirm your email so tomorrow's question can arrive. Check your inbox for a link from The Living Book.
            </p>
          </div>
        )}

        {error && (
          <p className="mb-6 text-red-700 text-center" style={{ fontFamily: "Georgia, serif", fontSize: "16px" }} role="alert" data-testid="mlb-error">
            {error}
          </p>
        )}

        {/* Overall progress line — visible + not colour-only */}
        <p className="text-center label-ui text-[11px] text-[#5C5548] tracking-[0.25em] mb-10" data-testid="mlb-progress">
          {doneCount} OF 7 ANSWERED
        </p>

        {/* Seven days in order */}
        <ol className="space-y-6" data-testid="mlb-day-list">
          {DAYS.map((d) => {
            const key = String(d.day);
            const entry = days[key];
            const isAnswered = !!entry && !!(entry.text || "").trim();
            const wasSkipped = skipped.has(d.day);
            const isCurrent = !isAnswered && !wasSkipped && d.day === availableDay;
            const isLocked = !isAnswered && !wasSkipped && d.day > availableDay;
            const isNextLocked = isLocked && d.day === availableDay + 1;
            const perDayUnlock = dayUnlockAt[String(d.day)] || null;
            return (
              <DayCard
                key={d.day}
                day={d.day}
                chapter={d.chapter}
                question={d.question}
                entry={entry}
                skipped={wasSkipped}
                isAnswered={isAnswered}
                isCurrent={isCurrent}
                isLocked={isLocked}
                isNextLocked={isNextLocked}
                perDayUnlockAt={perDayUnlock}
                formatUnlock={formatUnlockShort}
                backend={BACKEND}
                onOpen={() => navigate(`/story-week/day/${d.day}`)}
              />
            );
          })}
        </ol>

        <div className="mt-14 text-center">
          <Link to="/" className="label-ui text-[12px] text-[#5C5548] hover:text-[#B8973A] transition-colors inline-flex items-center gap-2" style={{ minHeight: 44 }} data-testid="mlb-footer-home">
            Back to The Living Book homepage
          </Link>
        </div>
      </main>
    </div>
  );
}

/** Card for a single day. All state (answered / ready / locked / skipped) is
 *  conveyed by icon + text label — never by colour alone. */
function DayCard({ day, chapter, question, entry, skipped, isAnswered, isCurrent, isLocked, isNextLocked, perDayUnlockAt, formatUnlock, backend, onOpen }) {
  return (
    <li className="bg-white hairline-cream" style={{ borderRadius: 4 }} data-testid={`mlb-day-${day}`}>
      <div className="p-5 sm:p-7">
        {/* Chapter kicker */}
        {chapter && (
          <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em] mb-2" data-testid={`mlb-day-${day}-chapter`}>
            {chapter.toUpperCase()}
          </p>
        )}
        {/* Status row */}
        <div className="flex items-center gap-3 mb-3">
          <StatusBadge state={isAnswered ? "answered" : skipped ? "skipped" : isCurrent ? "ready" : "locked"} />
          <span className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em]" data-testid={`mlb-day-${day}-label`}>
            DAY {day}
          </span>
        </div>

        {/* Question — hidden for locked days */}
        {!isLocked && (
          <p className="text-[#111D33] mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(1.2rem, 2.4vw, 1.5rem)", lineHeight: 1.4 }} data-testid={`mlb-day-${day}-question`}>
            &ldquo;{question}&rdquo;
          </p>
        )}

        {/* Answered state — preview + photo + audio + edit */}
        {isAnswered && (
          <>
            <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.65 }} data-testid={`mlb-day-${day}-preview`}>
              {truncate(entry.text || "", 220)}
            </p>

            {entry.photo_path && (
              <div className="mb-4 flex items-center gap-3" data-testid={`mlb-day-${day}-photo-wrap`}>
                <img
                  src={`${backend}/api/files/${encodeURI(entry.photo_path)}`}
                  alt={`Your photograph attached to Day ${day}`}
                  className="editorial-photo"
                  style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 2, background: "#F7F0E0" }}
                  data-testid={`mlb-day-${day}-photo`}
                />
                <span className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em] inline-flex items-center gap-2">
                  <ImageIcon size={14} strokeWidth={1.5} aria-hidden="true" />
                  <span>PHOTOGRAPH ADDED</span>
                </span>
              </div>
            )}

            {entry.audio_path && (
              <AudioPlayer path={entry.audio_path} backend={backend} day={day} />
            )}

            <div className="mt-5">
              <button
                type="button"
                onClick={onOpen}
                className="btn-outline-navy w-full sm:w-auto"
                style={{ minHeight: 48, padding: "12px 22px", fontSize: "14px", letterSpacing: "0.1em" }}
                data-testid={`mlb-day-${day}-edit-btn`}
              >
                READ OR EDIT MY ANSWER
              </button>
            </div>
          </>
        )}

        {/* Skipped state */}
        {skipped && (
          <>
            <p className="text-[#5C5548] mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.65 }} data-testid={`mlb-day-${day}-skipped-note`}>
              You chose to skip this question. You can come back to it any time.
            </p>
            <button
              type="button"
              onClick={onOpen}
              className="btn-outline-navy w-full sm:w-auto"
              style={{ minHeight: 48, padding: "12px 22px", fontSize: "14px", letterSpacing: "0.1em" }}
              data-testid={`mlb-day-${day}-answer-skipped-btn`}
            >
              ANSWER THIS QUESTION
            </button>
          </>
        )}

        {/* Ready-now state */}
        {isCurrent && (
          <>
            <p className="text-[#1B2A4A] mb-5" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.65 }} data-testid={`mlb-day-${day}-ready-note`}>
              This question is ready for you now. Take your time&mdash;there is no hurry.
            </p>
            <button
              type="button"
              onClick={onOpen}
              className="btn-gold w-full sm:w-auto"
              style={{ minHeight: 52, padding: "14px 24px", fontSize: "14px", letterSpacing: "0.1em" }}
              data-testid={`mlb-day-${day}-answer-btn`}
            >
              ANSWER THIS QUESTION
            </button>
          </>
        )}

        {/* Locked state — only the next locked day shows a date */}
        {isLocked && (
          <p className="text-[#5C5548]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.65 }} data-testid={`mlb-day-${day}-locked-note`}>
            {isNextLocked && perDayUnlockAt
              ? formatUnlock(perDayUnlockAt)
              : `Opens after Day ${day - 1} is answered`}
          </p>
        )}
      </div>
    </li>
  );
}

/** Status badge — every state uses an icon AND a text label (never colour
 *  alone). Screenreader-friendly. */
function StatusBadge({ state }) {
  const map = {
    answered: {
      Icon: Check, iconColor: "#2E7D5B", bg: "#EAF3EE", border: "#B7D6C4", text: "Answered", testid: "mlb-status-answered",
    },
    ready: {
      Icon: null, iconColor: "#B8973A", bg: "#FFF7E5", border: "#E8D5A3", text: "Ready now", testid: "mlb-status-ready",
    },
    locked: {
      Icon: Lock, iconColor: "#5C5548", bg: "#F2EEE6", border: "#D9CFBE", text: "Not available yet", testid: "mlb-status-locked",
    },
    skipped: {
      Icon: null, iconColor: "#5C5548", bg: "#F2EEE6", border: "#D9CFBE", text: "Skipped", testid: "mlb-status-skipped",
    },
  };
  const s = map[state] || map.locked;
  return (
    <span
      className="inline-flex items-center gap-2 py-1 px-3"
      style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 3, fontFamily: "Georgia, serif", fontSize: "14px", color: "#1B2A4A", minHeight: 30 }}
      data-testid={s.testid}
    >
      {s.Icon ? <s.Icon size={14} strokeWidth={2} color={s.iconColor} aria-hidden="true" /> : <span aria-hidden="true" style={{ color: s.iconColor }}>●</span>}
      <span>{s.text}</span>
    </span>
  );
}

/** Private audio player. The <audio> element sends the session cookie
 *  automatically (withCredentials setup). Backend applies /api/files
 *  ownership checks and returns 401/404 if the caller does not own the
 *  Story Week session. */
function AudioPlayer({ path, backend, day }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  const toggle = async () => {
    const el = ref.current;
    if (!el) return;
    setFailed(false);
    try {
      if (el.paused) {
        await el.play();
        setPlaying(true);
      } else {
        el.pause();
        setPlaying(false);
      }
    } catch (e) {
      setFailed(true);
      setPlaying(false);
    }
  };

  return (
    <div className="flex items-center gap-3 mb-4" data-testid={`mlb-day-${day}-audio-wrap`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-2 py-2 px-4 hairline"
        style={{ background: "white", borderColor: "#E8D5A3", borderRadius: 4, minHeight: 44, fontFamily: "Georgia, serif", fontSize: "15px", color: "#1B2A4A" }}
        aria-label={playing ? `Pause my recording for Day ${day}` : `Listen to my recording for Day ${day}`}
        data-testid={`mlb-day-${day}-listen-btn`}
      >
        {playing ? <PauseCircle size={20} strokeWidth={1.5} color="#B8973A" aria-hidden="true" /> : <PlayCircle size={20} strokeWidth={1.5} color="#B8973A" aria-hidden="true" />}
        <span>{playing ? "Pause" : "Listen to my recording"}</span>
      </button>
      <audio
        ref={ref}
        src={`${backend}/api/files/${encodeURI(path)}`}
        preload="none"
        onEnded={() => setPlaying(false)}
        onError={() => { setFailed(true); setPlaying(false); }}
        data-testid={`mlb-day-${day}-audio`}
      />
      {failed && (
        <span className="text-red-700" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }} role="alert" data-testid={`mlb-day-${day}-audio-error`}>
          We couldn't play your recording.
        </span>
      )}
    </div>
  );
}

function truncate(s, n) {
  const clean = (s || "").replace(/\s+/g, " ").trim();
  if (clean.length <= n) return clean;
  return clean.slice(0, n).replace(/\s+\S*$/, "") + "…";
}
